import type { MouseEvent } from 'react';
import { useCallback, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { IconButtonProps } from '@mui/material';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';

import { useLazyGetInvoiceUrlsQuery } from '../store/api';

export interface CopyInvoiceLinkIconButtonProps {
  patientId: string;
  invoiceId: string;
  fallbackUrl?: string | null;
  disabled?: boolean;
  size?: IconButtonProps['size'];
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    // Clipboard API is not available in a few embedded/browser-test contexts.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if (!document.execCommand('copy')) {
      throw new Error('copy_command_failed');
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

export function CopyInvoiceLinkIconButton({
  patientId,
  invoiceId,
  fallbackUrl,
  disabled = false,
  size = 'small',
}: CopyInvoiceLinkIconButtonProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [fetchInvoiceUrls] = useLazyGetInvoiceUrlsQuery();
  const [isCopying, setIsCopying] = useState(false);

  const handleCopy = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (disabled || isCopying) return;

      setIsCopying(true);
      try {
        let paymentUrl: string | null | undefined;

        try {
          const result = await fetchInvoiceUrls({ patientId, invoiceId }).unwrap();
          paymentUrl = result.payment_url;
        } catch {
          paymentUrl = fallbackUrl;
        }

        if (!paymentUrl) {
          throw new Error('missing_invoice_payment_url');
        }

        await copyTextToClipboard(paymentUrl);
        enqueueSnackbar('Invoice link copied', { variant: 'success' });
      } catch {
        enqueueSnackbar('Could not copy invoice link', { variant: 'error' });
      } finally {
        setIsCopying(false);
      }
    },
    [disabled, enqueueSnackbar, fallbackUrl, fetchInvoiceUrls, invoiceId, isCopying, patientId],
  );

  return (
    <Tooltip title="Copy invoice link">
      <span>
        <IconButton
          aria-label="Copy invoice link"
          size={size}
          onClick={handleCopy}
          disabled={disabled || isCopying}
          sx={{ color: 'text.secondary' }}
        >
          {isCopying ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <ContentCopyIcon fontSize="inherit" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default CopyInvoiceLinkIconButton;
